import type { PlatformAdapter } from './platformAdapter.interface';
import type { AuthPayload, Diagnostics } from '../platform.types';
import vkBridge from '@vkontakte/vk-bridge';
import { setAppHeight, applyViewportVars } from '../../shared/utils/viewport';
import { applyVKLaunchParamsFromBridge } from '../vk/launchParams';

export function parseQuery(): { rawQuery: string; params: Record<string, string> } {
  if (typeof window === 'undefined') return { rawQuery: '', params: {} };
  const rawQuery = (window.location.search || window.location.hash || '').replace(/^[#?]/, '').replace(/^\/+/, '');
  const urlParams = new URLSearchParams(rawQuery);
  const params: Record<string, string> = {};
  for (const [key, value] of urlParams) params[key] = value;
  return { rawQuery, params };
}

function applyVKViewport(data: { viewport_height?: number; viewport_width?: number; insets?: { top?: number; right?: number; bottom?: number; left?: number } } | undefined): void {
  if (!data) return;
  const vh = typeof data.viewport_height === 'number' && data.viewport_height > 0 ? data.viewport_height : undefined;
  if (data.insets && typeof data.insets === 'object') {
    applyViewportVars(vh != null ? { height: vh, insets: data.insets } : { insets: data.insets });
  } else if (vh != null) {
    setAppHeight(vh);
  }
}

type VKInitState = {
  initOk: boolean;
  launchParams: Record<string, any> | null;
};

let vkInitPromise: Promise<VKInitState> | null = null;
let vkConfigSubscribed = false;

/**
 * Единая (idempotent) инициализация VK Bridge.
 * Это SSOT для VK init: любой код вызывает только эту функцию.
 */
export async function ensureVKBridgeInit(): Promise<VKInitState> {
  if (vkInitPromise) return vkInitPromise;

  vkInitPromise = (async () => {
    try {
      let initOk = false;
      let launchParams: Record<string, any> | null = null;

      try {
        await vkBridge.send('VKWebAppInit');
        initOk = true;
      } catch {
        initOk = false;
      }

      if (!vkConfigSubscribed) {
        try {
          vkBridge.subscribe((event: any) => {
            if (event?.detail?.type === 'VKWebAppUpdateConfig') applyVKViewport(event.detail?.data);
          });
          vkConfigSubscribed = true;
        } catch {
          /* ignore */
        }
      }

      try {
        const config = (await Promise.race([
          vkBridge.send('VKWebAppGetConfig'),
          new Promise((_, reject) => setTimeout(() => reject(new Error('GetConfig timeout')), 2000)),
        ])) as any;
        applyVKViewport(config);
      } catch {
        setAppHeight();
      }

      const buildLaunchParams = (lp: Record<string, any> | null): Record<string, any> | null => {
        if (!lp || Object.keys(lp).length === 0) return null;
        const rawQuery = Object.entries(lp)
          .map(([key, value]) => `${key}=${encodeURIComponent(String(value))}`)
          .join('&');
        return { ...lp, rawQuery };
      };

      let lpForDetect: Record<string, any> | null = null;
      try {
        const lp = (await vkBridge.send('VKWebAppGetLaunchParams')) as Record<string, any> | null;
        if (lp && Object.keys(lp).length > 0) {
          launchParams = buildLaunchParams(lp);
          lpForDetect = lp;
        }
      } catch {
        /* ignore */
      }

      if (!launchParams) {
        const { params, rawQuery } = parseQuery();
        if (Object.keys(params).length > 0) {
          launchParams = { ...params, rawQuery };
          lpForDetect = params;
        }
      }

      if (lpForDetect) {
        applyVKLaunchParamsFromBridge(lpForDetect);
      }

      return { initOk, launchParams };
    } catch {
      return { initOk: false, launchParams: null };
    }
  })();

  return vkInitPromise;
}

export class VKAdapter implements PlatformAdapter {
  platform = 'vk' as const;
  private bridgeAvailable = false;
  private initOk = false;
  private launchParams: Record<string, any> | null = null;

  canDetect(): boolean {
    return true; // можем попробовать bridge даже если не в webview
  }

  async init(): Promise<void> {
    this.bridgeAvailable = true;
    const state = await ensureVKBridgeInit();
    this.initOk = state.initOk;
    this.launchParams = state.launchParams;
  }

  async getAuthPayload(): Promise<AuthPayload | null> {
    if (!this.launchParams || Object.keys(this.launchParams).length === 0) return null;
    return { kind: 'vk', payload: this.launchParams };
  }

  getDiagnostics(): Diagnostics {
    const { params, rawQuery } = parseQuery();
    return {
      bridgeAvailable: this.bridgeAvailable,
      initOk: this.initOk,
      launchParamsPresent: !!this.launchParams,
      launchParamsKeys: this.launchParams ? Object.keys(this.launchParams) : [],
      urlQuery: params,
      urlRawQuery: rawQuery,
    };
  }

  close(): void {
    void vkBridge.send('VKWebAppClose').catch(() => {
      // no-op
    });
  }
}
